import { Box, Button, Skeleton, Stack, Typography } from '@mui/material'
import { CompanyForm } from './company-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { FormProvider, useForm } from 'react-hook-form'
import { companyFormSchema, CompanyFormData } from './schema'
import { generatePath, useOutletContext, useNavigate } from 'react-router-dom'
import { ReadDealResponse } from '@/types'
import { useMemo, useRef, useEffect } from 'react'
import { routes } from '@/router'
import { LoadingButton } from '@mui/lab'
import {
  useUpdateCompany,
  useReadCompany,
  useListPartners,
  useReadCompanySerpro,
  useCreateCompanyTaxRegime,
} from '@/api/accounting-dealer/company'
import { sanitizeCnpj, toast, cleanObject } from '@/helpers'
import { queryClient } from '@/api/react-query'

export const HiringCompanyPage = () => {
  const { deal } = useOutletContext<ReadDealResponse>()
  const navigate = useNavigate()
  const formRef = useRef<HTMLFormElement>(null)

  const { data: company, isLoading: isCompanyLoading } = useReadCompany({
    companyKey: deal.company.key,
  })

  const { data: partners } = useListPartners({
    companyKey: deal.company.key,
  })

  const mainPartner = useMemo(
    () => partners?.find(({ is_main }) => is_main),
    [partners],
  )

  const { mutateAsync: updateCompany, isPending: isUpdatingCompany } =
    useUpdateCompany({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['company', deal.company.key],
        })
      },
    })

  const {
    mutateAsync: createCompanyTaxRegime,
    isPending: isCreatingTaxRegime,
  } = useCreateCompanyTaxRegime()

  const isChangingAccountant = company?.status === 'changing_accountant'
  
  const defaultCorporateName = useMemo(() => {
    if (isChangingAccountant || !mainPartner?.name) return ''
    return `${mainPartner.name} LTDA`
  }, [mainPartner?.name, isChangingAccountant])

  const form = useForm<CompanyFormData>({
    resolver: zodResolver(companyFormSchema),
    defaultValues: {
      tax_id: '',
      corporate_name: '',
      address: {
        zip_code: '',
        street: '',
        number: '',
        complement: '',
        district: '',
        ibge_code: '',
        state_code: '',
      },
    },
    values: company
      ? ({
          ...(cleanObject(company) as CompanyFormData),
          tax_id:
            company.tax_id || deal.deal_lead?.company_tax_id || '',
        } as CompanyFormData)
      : undefined,
  })

  const taxId = form.watch('tax_id') ?? ''
  const cleanTaxIdForSerpro = sanitizeCnpj(taxId)
  const isTaxIdCompleteForSerpro = cleanTaxIdForSerpro.length === 14

  const { data: serproData } = useReadCompanySerpro(
    { companyKey: deal.company.key, taxId: cleanTaxIdForSerpro },
    { enabled: !!isChangingAccountant && isTaxIdCompleteForSerpro },
  )

  useEffect(() => {
    if (company?.corporate_name) {
      form.setValue('corporate_name', company.corporate_name)
    } else if (!isChangingAccountant) {
      form.setValue('corporate_name', defaultCorporateName)
    }
  }, [
    defaultCorporateName,
    form,
    company?.corporate_name,
    isChangingAccountant,
  ])

  const onSubmit = async (data: CompanyFormData) => {
    await updateCompany({
      companyKey: deal.company.key,
      companyData: {
        corporate_name: data.corporate_name,
        address: data.address
          ? {
              ...data.address,
              ibge_code: data.address.ibge_code ?? '',
              complement: data.address.complement?.slice(0, 30) ?? '',
            }
          : undefined,
        tax_id: data.tax_id ? sanitizeCnpj(data.tax_id) : undefined,
      },
    })

    if (isChangingAccountant) {
      await createCompanyTaxRegime({
        companyKey: deal.company.key,
        taxRegimeData: serproData?.simple_tax_regime ? serproData?.simple_tax_regime : {
          special_tax_regime: 'without_special_tax_regime',
          tax_regime: 'normal_assumed_profit',
        },
      })

    }

    queryClient.invalidateQueries({
      queryKey: ['company', deal.company.key],
    })
    toast.success('Empresa atualizada com sucesso!')
    navigate(generatePath(routes.accounting.hiring.documents, { deal_key: deal.key }))
  }

  return (
    <Box>
      <Typography variant="h3" mb={6} textAlign="center">
        Dados da empresa
      </Typography>

      {isCompanyLoading ? (
        <Loading />
      ) : (
        <FormProvider {...form}>
          <CompanyForm
            ref={formRef}
            onSubmit={onSubmit}
            isChangingAccountant={!!isChangingAccountant}
            mainPartnerKey={mainPartner?.key}
          />
        </FormProvider>
      )}

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mt={8}
      >
        <Button
          variant="outlined"
          size="large"
          onClick={() =>
            navigate(
              generatePath(routes.accounting.hiring.mainPartner, { deal_key: deal.key }),
            )
          }
        >
          Voltar
        </Button>
        <LoadingButton
          type="submit"
          size="large"
          variant="contained"
          loading={isUpdatingCompany || isCreatingTaxRegime}
          onClick={() => formRef.current?.requestSubmit()}
        >
          Avançar
        </LoadingButton>
      </Stack>
    </Box>
  )
}

const Loading = () => (
  <Box>
    <Skeleton variant="rounded" height={56} sx={{ mb: 2 }} />
    <Stack direction="row" gap={2} sx={{ mb: 2 }}>
      <Skeleton variant="rounded" height={56} width="50%" />
      <Skeleton variant="rounded" height={56} width="50%" />
    </Stack>
    <Skeleton variant="rounded" height={56} sx={{ mb: 2 }} />
  </Box>
)
